import React, { useLayoutEffect, useMemo, useRef } from 'react';

/**
 * Khung tên "banner" bằng ẢNH (chỗ TO — trang cá nhân) cho các bậc cao:
 *  - Thần Thoại (Mythic): khung rồng vũ trụ.
 *  - Huyền Thoại (Legendary): khung phượng hoàng lửa.
 * Tên căn GIỮA panel + TỰ THU NHỎ font nếu dài, phủ mưa tia lửa/tro tàn.
 * Vị trí panel/khung khác nhau theo từng khung → truyền qua `cfg` (inline style),
 * dùng chung class .mnt-* nên không đụng tới style của khung mythic đang chạy.
 */
export interface BannerCfg {
  frameImg: string;
  aspect: string; // "824 / 305"
  slot: { left: string; top: string; width: string };
  /** Vùng mưa tia lửa (chỉ khung lửa: rồng/phượng). Bỏ trống → không có tia lửa. */
  magma?: { left: string; right: string; bottom: string; height: string };
  /** Class thêm cho tên (vd 'mnt-name-adaptive' — chữ đổi màu theo sáng/tối, cho khung tâm trong suốt). */
  nameClass?: string;
  /** true → hiệu ứng "PURE SMOOTH BLAZE" (Thần Thoại): lửa bùng theo chu kỳ 10s + tro tàn đồng bộ (theo test-hieuung). */
  blaze?: boolean;
  /** bảng màu → hạt bụi xoay quanh viền chữ nhật (Cổ Điển/Hiếm/Sử Thi theo avathap). Bỏ trống = không có. */
  dust?: string[];
  baseFont?: number;
}

// Toạ độ hạt bụi xoay quanh viền chữ nhật (superellipse) — tính cho banner FULL 440px; bản compact scale bằng CSS.
const DUST_W = 195; // bán kính ngang (theo avathap, wrapper 440)
function rectDust(palette: string[], bannerH: number) {
  const hh = bannerH * 0.33; // bán kính dọc theo chiều cao banner
  const coord = (t: number, w: number, h: number) => {
    const a = t * Math.PI * 2;
    const p = 0.65;
    return {
      x: w * Math.sign(Math.cos(a)) * Math.pow(Math.abs(Math.cos(a)), p),
      y: h * Math.sign(Math.sin(a)) * Math.pow(Math.abs(Math.sin(a)), p),
    };
  };
  const rnd = () => Math.random();
  return Array.from({ length: 50 }, (_, i) => {
    const off = (rnd() - 0.5) * 12;
    const w = DUST_W + off;
    const h = hh + off;
    const ts = rnd();
    const pts = [ts, (ts + 0.15) % 1, (ts + 0.85) % 1, (ts + 1) % 1].map((t) => coord(t, w, h));
    return {
      size: +(rnd() * 2.2 + 1).toFixed(1),
      color: palette[Math.floor(rnd() * palette.length)],
      glow: +(rnd() * 6 + 3).toFixed(1),
      duration: +(rnd() * 3.5 + 5.5).toFixed(2),
      delay: +(rnd() * -10).toFixed(2),
      maxOpacity: +(rnd() * 0.4 + 0.6).toFixed(2),
      pts,
      key: i,
    };
  });
}

// Mưa tia lửa: vị trí (%) + cỡ (v: alt=to/cam, sm=nhỏ/trắng) + tốc độ + trễ — phân bố dày khắp vùng viết.
const SPARKS: { l: number; b: number; d: number; dl: number; v?: 'alt' | 'sm' }[] = [
  { l: 4, b: 2, d: 2.1, dl: 0.1 }, { l: 10, b: 5, d: 2.7, dl: 0.8, v: 'alt' },
  { l: 16, b: 1, d: 2.3, dl: 0.3, v: 'sm' }, { l: 22, b: 6, d: 3.1, dl: 1.2 },
  { l: 28, b: 3, d: 2.0, dl: 0.5, v: 'alt' }, { l: 34, b: 4, d: 2.8, dl: 1.0 },
  { l: 40, b: 2, d: 2.5, dl: 0.2, v: 'sm' }, { l: 46, b: 5, d: 3.0, dl: 1.4, v: 'alt' },
  { l: 52, b: 1, d: 2.2, dl: 0.7 }, { l: 58, b: 6, d: 2.6, dl: 0.4, v: 'sm' },
  { l: 64, b: 3, d: 2.9, dl: 1.1, v: 'alt' }, { l: 70, b: 4, d: 2.4, dl: 0.6 },
  { l: 76, b: 2, d: 2.7, dl: 0.9, v: 'alt' }, { l: 82, b: 5, d: 2.1, dl: 0.2, v: 'sm' },
  { l: 88, b: 1, d: 3.2, dl: 1.3 }, { l: 94, b: 4, d: 2.3, dl: 0.5, v: 'alt' },
  { l: 13, b: 4, d: 2.4, dl: 1.6 }, { l: 43, b: 2, d: 2.7, dl: 0.3, v: 'sm' },
  { l: 73, b: 5, d: 2.2, dl: 0.9, v: 'alt' }, { l: 91, b: 3, d: 2.5, dl: 1.2 },
];

const ImageNametagBanner: React.FC<{ cfg: BannerCfg; children: React.ReactNode; className?: string }> = ({
  cfg,
  children,
  className,
}) => {
  const nameRef = useRef<HTMLSpanElement>(null);

  // Tự thu nhỏ cỡ chữ đến khi tên vừa vùng viết (slot) — không tràn ra khung.
  useLayoutEffect(() => {
    const el = nameRef.current;
    const slot = el?.parentElement;
    if (!el || !slot) return;
    let fs = cfg.baseFont ?? 22;
    el.style.fontSize = fs + 'px';
    // Thu nhỏ font vừa khung, nhưng KHÔNG nhỏ hơn 14px (đọc được). Còn dài quá → .mnt-name cắt "…".
    let guard = 0;
    while (el.scrollWidth > slot.clientWidth && fs > 14 && guard < 40) {
      fs -= 1;
      el.style.fontSize = fs + 'px';
      guard++;
    }
    // Tên dài hơn khung (đã cắt "…") → cho rê chuột xem đầy đủ (tooltip = tên gốc).
    const full = (el.textContent || '').trim();
    if (full && el.scrollWidth > slot.clientWidth + 1) {
      slot.setAttribute('title', full);
    } else {
      slot.removeAttribute('title');
    }
  });

  // Hạt bụi xoay quanh viền (Cổ Điển/Hiếm/Sử Thi). Tính 1 lần theo banner FULL; compact scale bằng CSS.
  const dust = useMemo(() => {
    if (!cfg.dust) return [];
    const [aw, ah] = cfg.aspect.split('/').map((s) => parseFloat(s));
    const bannerH = aw && ah ? (440 * ah) / aw : 150;
    return rectDust(cfg.dust, bannerH);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.dust, cfg.aspect]);

  return (
    <span
      className={`mnt-wrap ${className || ''}`}
      style={{
        // blaze: khung là <img> đè LÊN lửa (lửa nằm SAU khung) → không set background ở đây.
        backgroundImage: cfg.blaze ? undefined : `url(${cfg.frameImg})`,
        aspectRatio: cfg.aspect,
      }}
    >
      {cfg.blaze && (
        <>
          <span className="mnt-fire-aura" aria-hidden />
          <span className="mnt-fire-core" aria-hidden />
          {/* khung rồng đè lên lửa → rồng sắc nét phía trước, lửa cháy halo phía sau */}
          <img src={cfg.frameImg} className="mnt-frame-blaze" alt="" aria-hidden />
        </>
      )}
      {cfg.magma && (
        <span
          className="mnt-magma"
          aria-hidden
          style={{ left: cfg.magma.left, right: cfg.magma.right, bottom: cfg.magma.bottom, height: cfg.magma.height }}
        >
          {SPARKS.map((s, i) =>
            cfg.blaze ? (
              // tro tàn đồng bộ chu kỳ blaze 10s (chỉ bay lên khi lửa cháy)
              <span
                key={i}
                className="mnt-spark-blaze"
                style={{ left: `${s.l}%`, bottom: `${s.b}px`, animationDelay: `${(4 + (s.dl / 1.6) * 2.4).toFixed(2)}s` }}
              />
            ) : (
              <span
                key={i}
                className={`mnt-spark${s.v === 'alt' ? ' mnt-sp-alt' : s.v === 'sm' ? ' mnt-sp-sm' : ''}`}
                style={{
                  left: `${s.l}%`,
                  bottom: `${s.b}px`,
                  animationDuration: `${s.d}s`,
                  animationDelay: `${s.dl}s`,
                }}
              />
            )
          )}
        </span>
      )}
      {cfg.dust && (
        <span className="mnt-dust" aria-hidden>
          {dust.map((p) => (
            <span
              key={p.key}
              className="mnt-dust-p"
              style={{
                width: p.size,
                height: p.size,
                background: p.color,
                boxShadow: `0 0 ${p.glow}px ${p.color}`,
                animationDuration: `${p.duration}s`,
                animationDelay: `${p.delay}s`,
                ['--sx' as any]: `${p.pts[0].x.toFixed(1)}px`,
                ['--sy' as any]: `${p.pts[0].y.toFixed(1)}px`,
                ['--p1x' as any]: `${p.pts[1].x.toFixed(1)}px`,
                ['--p1y' as any]: `${p.pts[1].y.toFixed(1)}px`,
                ['--p2x' as any]: `${p.pts[2].x.toFixed(1)}px`,
                ['--p2y' as any]: `${p.pts[2].y.toFixed(1)}px`,
                ['--ex' as any]: `${p.pts[3].x.toFixed(1)}px`,
                ['--ey' as any]: `${p.pts[3].y.toFixed(1)}px`,
                ['--mo' as any]: p.maxOpacity,
              }}
            />
          ))}
        </span>
      )}
      <span className="mnt-slot" style={{ left: cfg.slot.left, top: cfg.slot.top, width: cfg.slot.width }}>
        <span
          ref={nameRef}
          className={`mnt-name [&_a]:!text-current [&_span]:!text-current ${cfg.nameClass || ''}`}
        >
          {children}
        </span>
      </span>
    </span>
  );
};

export default ImageNametagBanner;
